import { useId, useState } from "react";
import { LicenseIcon } from "../../icons/LicenseIcon";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import EventIcon from "@mui/icons-material/Event";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import NotesIcon from "@mui/icons-material/Notes";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StartIcon from "@mui/icons-material/Start";
import BookedIcon from "@mui/icons-material/ThumbUpOffAlt";
import InvitationIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, ReferenceField, useGetOne, useGetRecordRepresentation } from "react-admin";
import {
  ASFCourseSession,
  AutovioCalendarEvent,
  type CombinedExamResult,
  CourseLesson,
  DrivingLesson,
  getCombinedExamResult,
  hasBufferTimes,
  hasInstructor,
  isASFCourseSession,
  isCourseLesson,
  isDrivingLesson,
  isOtherEvent,
  isPracticalExam,
  isTheoryExam,
  isTheoryLesson,
  isVehicleEvent,
  OtherEvent,
  PracticalExam,
  TheoryExam,
  TheoryLesson,
  VehicleEvent,
} from "../../model/autovioCalendarEvents";
import {
  calendarEventTitle,
  formatDate,
  formatDateTimeRange,
  formatTime,
  otherEventDurationLabels,
  renderTheoryLessonSlots,
} from "../../utils/calendar";
import { AddressDisplay } from "../../misc/AddressDisplay";
import { BookedTrainingClassDisplay } from "../../misc/BookedTrainingClassDisplay";
import { InstructorAvatar } from "../../misc/InstructorAvatar";
import { Money } from "../../misc/Money";
import { StudentAvatar } from "../../misc/StudentAvatar";
import { StudentDisplayName } from "../../misc/StudentDisplayName";
import { Student } from "../../providers/studentsProvider";
import { grants } from "../../backoffice.access_control";
import { serverAPI } from "../../api/server.api";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { InstructorDisplayName } from "../../misc/InstructorDisplayName";
import { RoomIcon } from "../../icons/RoomIcon";
import { InstructorIcon } from "../../icons/InstructorIcon";
import { UserDisplayName } from "../../misc/UserDisplayName";
import { StudentLabels } from "../../misc/StudentLabels";
import { VehicleIcon } from "../../icons/VehicleIcon";
import { AttendeesList } from "./AttendeesList";
import { EventInfoRow } from "./EventInfoRow";
import { l10n } from "../../l10n";
import { Vehicle } from "../../providers/vehiclesProvider";
import { PaymentSucceededIcon } from "../../icons/PaymentSucceededIcon";
import { PaymentFailedIcon } from "../../icons/PaymentFailedIcon";
import { PaymentPendingIcon } from "../../icons/PaymentPendingIcon";
import { PaymentRefundedIcon } from "../../icons/PaymentRefundedIcon";

// TODO: Refactor into a separate component for the header box and for the changelog entries
type EventInfoCardProps = {
  chip?: React.ReactNode;
  changedKeys?: Array<string>;
  showStartedAtEndedAt?: boolean;
  mode?: "beforeChange" | "afterChange";
};

export const EventInfoCard = (props: { event: AutovioCalendarEvent } & EventInfoCardProps) => {
  const { event, ...restProps } = props;
  if (isTheoryLesson(event)) {
    return <TheoryLessonEventInfoCard event={event} {...restProps} />;
  } else if (isDrivingLesson(event)) {
    return <DrivingLessonEventInfoCard event={event} {...restProps} />;
  } else if (isTheoryExam(event)) {
    return <TheoryExamEventInfoCard event={event} {...restProps} />;
  } else if (isASFCourseSession(event)) {
    return <ASFCourseSessionEventInfoCard event={event} {...restProps} />;
  } else if (isCourseLesson(event)) {
    return <CourseLessonEventInfoCard event={event} {...restProps} />;
  } else if (isOtherEvent(event)) {
    return <OtherEventInfoCard event={event} {...restProps} />;
  } else if (isVehicleEvent(event)) {
    return <VehicleEventInfoCard event={event} {...restProps} />;
  } else {
    return <Typography color="error">Unbekannter Termin</Typography>;
  }
};

function _helpers(props: { mode?: "mainCard" | "beforeChange" | "afterChange"; changedKeys?: Array<string> }): {
  mode: "mainCard" | "beforeChange" | "afterChange";
  highlightProps: (
    key: string,
  ) =>
    | { isHighlighted?: never; highlightColor?: never }
    | { isHighlighted: true; highlightColor: "eventChangeBefore.main" | "eventChangeAfter.main" };
} {
  const mode: "mainCard" | "beforeChange" | "afterChange" = props.mode ?? "mainCard";
  if (mode === "mainCard") {
    return {
      mode,
      highlightProps: () => ({}),
    };
  }
  const { changedKeys } = props;
  if (!changedKeys) {
    throw new Error(`changedKeys must be provided for mode ${mode}`);
  }
  return {
    mode,
    highlightProps: (key: string) =>
      changedKeys.includes(key)
        ? {
            isHighlighted: true,
            highlightColor: mode === "beforeChange" ? "eventChangeBefore.main" : "eventChangeAfter.main",
          }
        : {},
  };
}

/** See https://zpl.io/m88Oyn3 */
const TheoryLessonEventInfoCard = (props: { event: TheoryLesson } & EventInfoCardProps) => {
  const { event } = props;
  const { mode } = _helpers(props);
  return (
    <Column spacing="0.5em" sx={mode === "mainCard" ? undefined : { "& a": { color: "inherit" } }}>
      <TheoryLessonDetails event={event} highlightingProps={{ mode: props.mode, changedKeys: props.changedKeys }} />
      {mode === "mainCard" && (
        <>
          <EventInfoRow tooltip="Anzahl Buchungen / Maximale Teilnehmerzahl">
            <PeopleOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="body2">{renderTheoryLessonSlots(event)} Fahrschüler</Typography>
          </EventInfoRow>
          <div style={{ height: "10px" }} />
          <AttendeesList event={event} />
          <div style={{ height: "20px" }} />
        </>
      )}
    </Column>
  );
};

export function TheoryLessonDetails(props: {
  event: TheoryLesson;
  highlightingProps?: {
    mode?: "beforeChange" | "afterChange";
    changedKeys?: Array<string>;
  };
}) {
  const { event, highlightingProps } = props;
  const { highlightProps } = _helpers({ ...highlightingProps });
  return (
    <>
      <EventInfoRow {...highlightProps("instructorId")} tooltip="Fahrlehrer">
        <_LinkToInstructor instructorId={event.instructorId} />
      </EventInfoRow>
      <EventInfoRow {...highlightProps("theoryUnit")} tooltip="Lektionsnummer">
        <FormatListNumberedIcon
          fontSize="small"
          sx={{ color: highlightProps("theoryUnit").highlightColor ?? "text.secondary" }}
        />
        <Typography variant="body2">{l10n.lectureXxx(event)}</Typography>
      </EventInfoRow>
      <EventInfoRow {...highlightProps("start")} tooltip="Beginn & Ende">
        <EventIcon fontSize="small" sx={{ color: highlightProps("start").highlightColor ?? "text.secondary" }} />
        <Typography variant="body2">{formatDateTimeRange(event.start, event.end)}</Typography>
      </EventInfoRow>
      {hasBufferTimes(event) && (
        <EventInfoRow {...highlightProps("bufferTimes")} tooltip="Vor- und Nachbereitungszeit">
          <MoreTimeIcon
            fontSize="small"
            sx={{ color: highlightProps("bufferTimes").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            {`Vorbereitungszeit ${event.bufferTimes?.preparationTime ?? 0} min.`}
            {" • "}
            {`Nachbereitungszeit ${event.bufferTimes?.wrapUpTime ?? 0} min.`}
          </Typography>
        </EventInfoRow>
      )}
      <EventInfoRow {...highlightProps("location")} tooltip="Ort des Theorieunterrichts">
        <PlaceOutlinedIcon
          fontSize="small"
          sx={{ color: highlightProps("location").highlightColor ?? "text.secondary" }}
        />
        <Typography variant="body2">
          {event.branchId && (
            <>
              <ReferenceField source="branchId" reference="branches" record={event} link={false} />
              {" • "}
            </>
          )}
          {event.location.type !== "OnlineMeeting" && <AddressDisplay address={event.location} oneLine />}
          {event.location.type === "OnlineMeeting" && (
            <Link to={event.location.url} target="_blank">
              Online
            </Link>
          )}
        </Typography>
      </EventInfoRow>
      {event.noteForInstructor && (
        <EventInfoRow {...highlightProps("noteForInstructor")} tooltip="Notizen">
          <NotesIcon
            fontSize="small"
            sx={{ color: highlightProps("noteForInstructor").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2">{event.noteForInstructor}</Typography>
        </EventInfoRow>
      )}
      {event.noteForStudents && (
        <EventInfoRow {...highlightProps("noteForStudents")} tooltip="Hinweis für Fahrschüler">
          <NotesIcon
            fontSize="small"
            sx={{ color: highlightProps("noteForStudents").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2">{event.noteForStudents}</Typography>
        </EventInfoRow>
      )}
    </>
  );
}

const DrivingLessonEventInfoCard = (props: { event: DrivingLesson } & EventInfoCardProps) => {
  const { event } = props;
  const { mode, highlightProps } = _helpers(props);
  const isFinished = event.endedAt !== undefined;
  const { data: student } = useGetOne<Student>(
    "students",
    { id: event.student.uid },
    { enabled: !isFinished && mode === "mainCard" },
  );
  const showOutstandingPaymentWarning = student && student.balance < 0 && !isFinished && mode === "mainCard";

  return (
    <Column
      spacing="0.5em"
      sx={{
        color: "text.secondary",
        "& a": { color: "inherit" },
        ...(mode === "mainCard" ? { bgcolor: "action.hover", borderRadius: 1, p: 2 } : {}),
      }}
    >
      {mode === "mainCard" && (
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          Details
        </Typography>
      )}
      {event.instructorId /* ... simulator driving lessons do not have an instructor */ && (
        <EventInfoRow {...highlightProps("instructorId")} tooltip="Fahrlehrer">
          <_LinkToInstructor instructorId={event.instructorId} />
        </EventInfoRow>
      )}
      {mode === "mainCard" /* ... do not display student in change log, because it cannot change */ && (
        <EventInfoRow tooltip="Fahrschüler">
          <_LinkToStudent studentId={event.student.uid} />
        </EventInfoRow>
      )}
      {showOutstandingPaymentWarning && (
        <EventInfoRow>
          <WarningAmberRoundedIcon fontSize="small" sx={{ color: "#d32f2f" }} />
          <Typography variant="body2" sx={{ color: "#d32f2f" }}>
            Fahrschüler hat offene Posten.
          </Typography>
        </EventInfoRow>
      )}
      {(props.changedKeys ?? []).includes("isBooked") && (
        <EventInfoRow {...highlightProps("isBooked")} tooltip="Status">
          {event.isBooked ? <BookedIcon fontSize="small" /> : <InvitationIcon fontSize="small" />}
          <Typography variant="body2">{event.isBooked ? "Gebucht" : "Einladung"}</Typography>
        </EventInfoRow>
      )}
      <EventInfoRow {...highlightProps("drivingLessonType")} tooltip="Art der Übungsstunde">
        <LabelOutlinedIcon
          fontSize="small"
          sx={{ color: highlightProps("drivingLessonType").highlightColor ?? "text.secondary" }}
        />
        <Typography variant="body2">{calendarEventTitle(event, { omitPrefix: true })}</Typography>
      </EventInfoRow>
      <EventInfoRow {...highlightProps("start")} tooltip="Beginn & Ende">
        <EventIcon fontSize="small" sx={{ color: highlightProps("start").highlightColor ?? "text.secondary" }} />
        <Typography variant="body2">{formatDateTimeRange(event.start, event.end)}</Typography>
      </EventInfoRow>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: "30px" }}>
        <EventInfoRow {...highlightProps("duration")} tooltip="Dauer">
          <ScheduleIcon
            fontSize="small"
            sx={{ color: highlightProps("duration").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            {`${Math.round(event.end.diff(event.start, "minutes").minutes)} min.`}
          </Typography>
        </EventInfoRow>
        {mode === "mainCard" && props.showStartedAtEndedAt && (
          <>
            <EventInfoRow tooltip="Gestartet um" tooltipPlacement="bottom">
              <StartIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography variant="body2" noWrap>
                {event.startedAt ? formatTime(event.startedAt) : "Nicht gestartet"}
              </Typography>
            </EventInfoRow>
            <EventInfoRow tooltip="Beendet um" tooltipPlacement="bottom">
              <KeyboardTabIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography variant="body2" noWrap>
                {event.endedAt ? formatTime(event.endedAt) : "Nicht beendet"}
              </Typography>
            </EventInfoRow>
          </>
        )}
      </Stack>
      {hasInstructor(event) && hasBufferTimes(event) && (
        <EventInfoRow {...highlightProps("bufferTimes")} tooltip="Transferzeiten">
          <MoreTimeIcon
            fontSize="small"
            sx={{ color: highlightProps("bufferTimes").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            Anfahrt {event.bufferTimes?.preparationTime ?? 0} min. • Abfahrt {event.bufferTimes?.wrapUpTime ?? 0} min.
          </Typography>
        </EventInfoRow>
      )}
      {event.student.bookedTrainingId && (
        <EventInfoRow {...highlightProps("bookedTrainingId")} tooltip="Zugewiesene Ausbildung">
          <LicenseIcon
            fontSize="small"
            sx={{ color: highlightProps("bookedTrainingId").highlightColor ?? "text.secondary" }}
          />
          <BookedTrainingClassDisplay studentId={event.student.uid} bookedTrainingId={event.student.bookedTrainingId} />
        </EventInfoRow>
      )}
      <EventInfoRow {...highlightProps("startLocation")} tooltip="Startpunkt">
        <PlaceOutlinedIcon
          fontSize="small"
          sx={{ color: highlightProps("startLocation").highlightColor ?? "text.secondary" }}
        />
        <Typography variant="body2">
          <AddressDisplay address={event.startLocation} oneLine />
        </Typography>
      </EventInfoRow>
      {event.studentUsesOwnMotorcycle && (
        <EventInfoRow {...highlightProps("studentUsesOwnMotorcycle")} tooltip="Motorrad für Fahrschüler">
          <VehicleIcon
            type="motorcycle"
            fontSize="small"
            htmlColor={highlightProps("studentUsesOwnMotorcycle").highlightColor}
          />
          <Typography sx={{ ml: ".5em" }} variant="body2">
            Eigenes Motorrad
          </Typography>
        </EventInfoRow>
      )}
      {event.vehicleId && (
        <EventInfoRow {...highlightProps("vehicleId")} tooltip={<_VehicleTooltip vehicleId={event.vehicleId} />}>
          <_LinkToVehicle vehicleId={event.vehicleId} highlightColor={highlightProps("vehicleId").highlightColor} />
        </EventInfoRow>
      )}
      {event.vehicle2Id && (
        <EventInfoRow {...highlightProps("vehicle2Id")} tooltip={<_Vehicle2Tooltip vehicle2Id={event.vehicle2Id} />}>
          <_LinkToVehicle vehicleId={event.vehicle2Id} highlightColor={highlightProps("vehicle2Id").highlightColor} />
        </EventInfoRow>
      )}
      {grants.has("viewPrice") && (
        <EventInfoRow {...highlightProps("price")} tooltip="Preis">
          <EuroRoundedIcon
            fontSize="small"
            sx={{ color: highlightProps("price").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            <Money value={event.price} />
          </Typography>
        </EventInfoRow>
      )}
      {event.noteForInstructor && (
        <EventInfoRow {...highlightProps("noteForInstructor")} tooltip="Notizen">
          <NotesIcon
            fontSize="small"
            sx={{ color: highlightProps("noteForInstructor").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2">{event.noteForInstructor}</Typography>
        </EventInfoRow>
      )}
      {event.noteForStudent && (
        <EventInfoRow {...highlightProps("noteForStudent")} tooltip="Hinweis für Fahrschüler">
          <NotesIcon
            fontSize="small"
            sx={{ color: highlightProps("noteForStudent").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2">{event.noteForStudent}</Typography>
        </EventInfoRow>
      )}
      {mode === "mainCard" &&
        grants.has("CalendarEvent:triggerPayment") &&
        (event.billingWhen === "asap" || event.paymentStatus) && (
          <EventInfoRow>
            {event.paymentStatus === "success" ? (
              <PaymentSucceededIcon fontSize="small" sx={{ color: "text.secondary" }} />
            ) : event.paymentStatus === "failed" || event.paymentStatus === "disputeLost" ? (
              <PaymentFailedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            ) : event.paymentStatus === "refunded" ? (
              <PaymentRefundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            ) : (
              <PaymentPendingIcon fontSize="small" sx={{ color: "text.secondary" }} />
            )}
            <Typography variant="body2">
              {event.paymentStatus === "success"
                ? "Termin ist bezahlt"
                : event.paymentStatus === "failed" || event.paymentStatus === "disputeLost"
                  ? "Bezahlung ist fehlgeschlagen"
                  : event.paymentStatus === "refunded"
                    ? "Bezahlung wurde zurückerstattet"
                    : "Bezahlung wurde ausgelöst"}
            </Typography>
          </EventInfoRow>
        )}
      {mode === "mainCard" && (
        <>
          {isPracticalExam(event) && (
            <EventInfoRow>
              <ExamResultForm exam={event} />
            </EventInfoRow>
          )}
          <EventExtensions event={event} />
        </>
      )}
    </Column>
  );
};

const TheoryExamEventInfoCard = (props: { event: TheoryExam } & EventInfoCardProps) => {
  const { event } = props;
  const { mode, highlightProps } = _helpers(props);
  const { startLocation: location } = event;
  return (
    <Column
      spacing="0.5em"
      sx={{
        color: "text.secondary",
        "& a": { color: "inherit" },
        ...(mode === "mainCard" ? { bgcolor: "action.hover", borderRadius: 1, p: 2 } : {}),
      }}
    >
      {mode === "mainCard" && (
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          Details
        </Typography>
      )}
      {mode === "mainCard" /* ... do not display student in change log, because it cannot change */ && (
        <EventInfoRow tooltip="Fahrschüler">
          <_LinkToStudent studentId={event.student.uid} />
        </EventInfoRow>
      )}
      <EventInfoRow {...highlightProps("start")} tooltip="Beginn & Ende">
        <EventIcon fontSize="small" sx={{ color: highlightProps("start").highlightColor ?? "text.secondary" }} />
        <Typography variant="body2">{formatDateTimeRange(event.start, event.end)}</Typography>
      </EventInfoRow>
      <Stack direction="row" spacing="30px">
        <EventInfoRow {...highlightProps("duration")} tooltip="Dauer">
          <ScheduleIcon
            fontSize="small"
            sx={{ color: highlightProps("duration").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            {`${Math.round(event.end.diff(event.start, "minutes").minutes)} min.`}
          </Typography>
        </EventInfoRow>
      </Stack>
      {event.student.bookedTrainingId && (
        <EventInfoRow {...highlightProps("bookedTrainingId")} tooltip="Zugewiesene Ausbildung">
          <LicenseIcon
            fontSize="small"
            sx={{ color: highlightProps("bookedTrainingId").highlightColor ?? "text.secondary" }}
          />
          <BookedTrainingClassDisplay studentId={event.student.uid} bookedTrainingId={event.student.bookedTrainingId} />
        </EventInfoRow>
      )}
      {(location.street || location.city) && (
        <EventInfoRow {...highlightProps("startLocation")} tooltip="Ort der Theorieprüfung">
          <PlaceOutlinedIcon
            fontSize="small"
            sx={{ color: highlightProps("startLocation").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2">
            <AddressDisplay address={event.startLocation} oneLine />
          </Typography>
        </EventInfoRow>
      )}
      {event.noteForInstructor && (
        <EventInfoRow {...highlightProps("noteForInstructor")} tooltip="Notizen">
          <NotesIcon
            fontSize="small"
            sx={{ color: highlightProps("noteForInstructor").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2">{event.noteForInstructor}</Typography>
        </EventInfoRow>
      )}
      {event.noteForStudent && (
        <EventInfoRow {...highlightProps("noteForStudent")} tooltip="Hinweis für Fahrschüler">
          <NotesIcon
            fontSize="small"
            sx={{ color: highlightProps("noteForStudent").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2">{event.noteForStudent}</Typography>
        </EventInfoRow>
      )}
      {mode === "mainCard" && (
        <EventInfoRow>
          <ExamResultForm exam={event} />
        </EventInfoRow>
      )}
    </Column>
  );
};

const ASFCourseSessionEventInfoCard = (props: { event: ASFCourseSession } & EventInfoCardProps) => {
  const { event } = props;
  const { mode } = _helpers(props);
  const participantCount = Object.keys(event.participants).length;
  const details = (
    <ASFCourseSessionDetails event={event} highlightingProps={{ mode: props.mode, changedKeys: props.changedKeys }} />
  );
  return (
    <Column
      spacing={mode === "mainCard" ? "1em" : "0.5em"}
      sx={{
        color: "text.secondary",
        "& a": { color: "inherit" },
      }}
    >
      {mode === "mainCard" ? (
        <>
          <Column spacing="0.5em" sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2 }}>
            <Typography variant="body2" fontWeight="bold" color="text.primary">
              Details
            </Typography>
            {details}
            <EventInfoRow tooltip="Anzahl Teilnehmer">
              <PeopleOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography variant="body2">{participantCount} Teilnehmer</Typography>
            </EventInfoRow>
          </Column>
          {participantCount > 0 && (
            <AttendeesList event={event} sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2 }} />
          )}
        </>
      ) : (
        details
      )}
    </Column>
  );
};

export function ASFCourseSessionDetails(props: {
  event: ASFCourseSession;
  highlightingProps?: {
    mode?: "beforeChange" | "afterChange";
    changedKeys?: Array<string>;
  };
}) {
  const { event, highlightingProps } = props;
  const { highlightProps } = _helpers({ ...highlightingProps });
  return (
    <>
      <EventInfoRow {...highlightProps("instructorId")} tooltip="Fahrlehrer">
        <_LinkToInstructor instructorId={event.instructorId} />
      </EventInfoRow>
      <EventInfoRow {...highlightProps("start")} tooltip="Beginn & Ende">
        <EventIcon fontSize="small" sx={{ color: highlightProps("start").highlightColor ?? "text.secondary" }} />
        <Typography variant="body2">{formatDateTimeRange(event.start, event.end)}</Typography>
      </EventInfoRow>
      {hasBufferTimes(event) && (
        <EventInfoRow {...highlightProps("bufferTimes")} tooltip="Vor- und Nachbereitungszeit">
          <MoreTimeIcon
            fontSize="small"
            sx={{ color: highlightProps("bufferTimes").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            {`Vorbereitungszeit ${event.bufferTimes?.preparationTime ?? 0} min.`}
            {" • "}
            {`Nachbereitungszeit ${event.bufferTimes?.wrapUpTime ?? 0} min.`}
          </Typography>
        </EventInfoRow>
      )}
      <EventInfoRow
        {...highlightProps("location")}
        tooltip={`Ort der ${event.sessionType === "fahrprobe" ? "Fahrprobe" : "Gruppensitzung"}`}
      >
        <PlaceOutlinedIcon
          fontSize="small"
          sx={{ color: highlightProps("location").highlightColor ?? "text.secondary" }}
        />
        <Typography variant="body2">
          <AddressDisplay address={event.location} oneLine />
        </Typography>
      </EventInfoRow>
    </>
  );
}

const CourseLessonEventInfoCard = (props: { event: CourseLesson } & EventInfoCardProps) => {
  const { event } = props;
  const { highlightProps } = _helpers(props);
  return (
    <Column spacing="0.5em">
      <EventInfoRow {...highlightProps("start")} tooltip="Beginn & Ende">
        <EventIcon fontSize="small" sx={{ color: highlightProps("start").highlightColor ?? "text.secondary" }} />
        <Typography variant="body2">{formatDateTimeRange(event.start, event.end)}</Typography>
      </EventInfoRow>
      <EventInfoRow {...highlightProps("lecturerId")} tooltip="Dozent">
        <InstructorIcon
          fontSize="small"
          sx={{ color: highlightProps("lecturerId").highlightColor ?? "text.secondary" }}
        />
        {event.lecturerId ? (
          <UserDisplayName userId={event.lecturerId} variant="body2" noWrap />
        ) : (
          <Typography component="span" variant="body2" sx={{ color: "text.disabled" }}>
            Noch kein Dozent zugewiesen
          </Typography>
        )}
      </EventInfoRow>
      {hasBufferTimes(event) && (
        <EventInfoRow {...highlightProps("bufferTimes")} tooltip="Vor- und Nachbereitungszeit">
          <MoreTimeIcon
            fontSize="small"
            sx={{ color: highlightProps("bufferTimes").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            {`Vorbereitungszeit ${event.bufferTimes?.preparationTime ?? 0} min.`}
            {" • "}
            {`Nachbereitungszeit ${event.bufferTimes?.wrapUpTime ?? 0} min.`}
          </Typography>
        </EventInfoRow>
      )}
      <EventInfoRow tooltip="Raum">
        <RoomIcon fontSize="small" sx={{ color: highlightProps("location").highlightColor ?? "text.secondary" }} />
        <Typography variant="body2">
          {event.location.type === "OnlineMeeting" ? (
            <Link to={event.location.url} target="_blank">
              Online
            </Link>
          ) : event.roomId ? (
            <ReferenceField reference="rooms" record={event} source="roomId" link={false} />
          ) : (
            <Typography component="span" variant="body2" sx={{ color: "text.disabled" }}>
              Noch kein Raum zugewiesen
            </Typography>
          )}
        </Typography>
      </EventInfoRow>
      {event.noteForParticipants ? (
        <EventInfoRow {...highlightProps("noteForParticipants")} tooltip="Hinweis für Teilnehmer">
          <NotesIcon
            fontSize="small"
            sx={{ color: highlightProps("noteForParticipants").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {event.noteForParticipants}
          </Typography>
        </EventInfoRow>
      ) : null}
    </Column>
  );
};

const OtherEventInfoCard = (props: { event: OtherEvent } & EventInfoCardProps) => {
  const { event } = props;
  const { mode, highlightProps } = _helpers(props);
  // @ts-expect-error TS2538
  const otherEventDurationLabel = otherEventDurationLabels[event.otherEventDuration];
  return (
    <Column
      spacing="0.5em"
      sx={{
        color: "text.secondary",
        "& a": { color: "inherit" },
        ...(mode === "mainCard" ? { bgcolor: "action.hover", borderRadius: 1, p: 2 } : {}),
      }}
    >
      {mode === "mainCard" && (
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          Details
        </Typography>
      )}
      <EventInfoRow {...highlightProps("instructorId")} tooltip="Fahrlehrer">
        <_LinkToInstructor instructorId={event.instructorId} />
      </EventInfoRow>
      {otherEventDurationLabel ? (
        <EventInfoRow {...highlightProps("otherEventDuration")} tooltip="Datum & Dauer">
          <EventIcon
            fontSize="small"
            sx={{ color: highlightProps("otherEventDuration").highlightColor ?? "text.secondary" }}
          />
          <Typography
            variant="body2"
            noWrap
          >{`${formatDate(event.start /* asToday: true */)} • ${otherEventDurationLabel}`}</Typography>
        </EventInfoRow>
      ) : (
        <EventInfoRow {...highlightProps("start")} tooltip="Beginn & Ende">
          <EventIcon fontSize="small" sx={{ color: highlightProps("start").highlightColor ?? "text.secondary" }} />
          <Typography variant="body2" noWrap>
            {formatDateTimeRange(event.start, event.end)}
          </Typography>
        </EventInfoRow>
      )}
      {hasBufferTimes(event) && (
        <EventInfoRow {...highlightProps("bufferTimes")} tooltip="Pufferzeiten">
          <MoreTimeIcon
            fontSize="small"
            sx={{ color: highlightProps("bufferTimes").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            Pufferzeiten: davor {event.bufferTimes?.preparationTime ?? 0} min., danach{" "}
            {event.bufferTimes?.wrapUpTime ?? 0} min.
          </Typography>
        </EventInfoRow>
      )}
      {event.vehicleId && (
        <EventInfoRow {...highlightProps("vehicleId")} tooltip={<_VehicleTooltip vehicleId={event.vehicleId} />}>
          <_LinkToVehicle vehicleId={event.vehicleId} highlightColor={highlightProps("vehicleId").highlightColor} />
        </EventInfoRow>
      )}
      {event.notes && (
        <EventInfoRow {...highlightProps("notes")} tooltip="Notiz">
          <NotesIcon fontSize="small" sx={{ color: highlightProps("notes").highlightColor ?? "text.secondary" }} />
          <Typography variant="body2">{event.notes}</Typography>
        </EventInfoRow>
      )}
    </Column>
  );
};

function VehicleEventInfoCard(props: { event: VehicleEvent } & EventInfoCardProps) {
  const { event } = props;

  const { mode, highlightProps } = _helpers(props);
  return (
    <Column
      spacing="0.5em"
      sx={{
        color: "text.secondary",
        "& a": { color: "inherit" },
        ...(mode === "mainCard" ? { bgcolor: "action.hover", borderRadius: 1, p: 2 } : {}),
      }}
    >
      {mode === "mainCard" && (
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          Details
        </Typography>
      )}
      <EventInfoRow {...highlightProps("start")} tooltip="Beginn & Ende">
        <EventIcon fontSize="small" sx={{ color: highlightProps("start").highlightColor ?? "text.secondary" }} />
        <Typography variant="body2" noWrap>
          {formatDateTimeRange(event.start, event.end)}
        </Typography>
      </EventInfoRow>
      {hasBufferTimes(event) && (
        <EventInfoRow {...highlightProps("bufferTimes")} tooltip="Pufferzeiten">
          <MoreTimeIcon
            fontSize="small"
            sx={{ color: highlightProps("bufferTimes").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2" noWrap>
            Pufferzeiten: davor {event.bufferTimes?.preparationTime ?? 0} min., danach{" "}
            {event.bufferTimes?.wrapUpTime ?? 0} min.
          </Typography>
        </EventInfoRow>
      )}
      <EventInfoRow {...highlightProps("vehicleId")} tooltip={<_VehicleTooltip vehicleId={event.vehicleId} />}>
        <_LinkToVehicle vehicleId={event.vehicleId} highlightColor={highlightProps("vehicleId").highlightColor} />
      </EventInfoRow>
      {event.noteForInstructor && (
        <EventInfoRow {...highlightProps("noteForInstructor")} tooltip="Notiz">
          <NotesIcon
            fontSize="small"
            sx={{ color: highlightProps("noteForInstructor").highlightColor ?? "text.secondary" }}
          />
          <Typography variant="body2">{event.noteForInstructor}</Typography>
        </EventInfoRow>
      )}
    </Column>
  );
}

const EventExtensions = ({ event }: { event: DrivingLesson }) => {
  const extensions = event.extensions;
  if (!extensions || extensions.length === 0) {
    return null;
  }

  return (
    <Stack spacing="10px">
      <Box height="5px" />
      <Typography variant="body2" fontWeight="bold">
        Verlängerungen
      </Typography>
      <Stack spacing="5px">
        {extensions.map((extension) => {
          const eventDuration = event.end.diff(event.start, "minutes").minutes;
          const extensionPrice = Math.round((event.price.amount * 100 * extension.durationInMinutes) / eventDuration);
          const originalEnd = formatTime(extension.newEnd.minus({ minutes: extension.durationInMinutes }));
          const newEnd = formatTime(extension.newEnd);
          return (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" key={extension.extendedAt.toISO()}>
                +{extension.durationInMinutes} min. von {originalEnd} bis {newEnd}
                {grants.has("viewPrice") && <> (+{<Money cents={extensionPrice} />})</>}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                um {formatTime(extension.extendedAt)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

function ExamResultForm(props: { exam: TheoryExam | PracticalExam }) {
  const { exam } = props;
  const [result, setResult] = useState<CombinedExamResult | "">(() => getCombinedExamResult(exam));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const labelId = useId();

  return (
    <Row gap={1} sx={{ minWidth: 0, pt: 1, width: { xs: "100%", sm: "auto" } }}>
      <FormControl
        size="small"
        sx={{
          flex: { xs: "1 1 auto", sm: "0 0 200px" },
          minWidth: 0,
          "& .MuiInputBase-root": { minWidth: 0 },
        }}
      >
        <InputLabel id={labelId}>Prüfungsergebnis</InputLabel>
        <Select
          labelId={labelId}
          label="Prüfungsergebnis"
          disabled={isSubmitting}
          value={result}
          onChange={async (event) => {
            const selectedResult = event.target.value as CombinedExamResult;
            setIsSubmitting(true);
            await runSafely(() => serverAPI.updateExamResult({ eventId: exam.id, result: selectedResult }), {
              errorMessage: "Fehler beim Speichern des Prüfungsergebnisses.",
              successMessage: "Prüfungsergebnis erfolgreich gespeichert.",
              onSuccess: () => setResult(selectedResult),
            });
            setIsSubmitting(false);
          }}
        >
          <MenuItem value="passed">Prüfung bestanden</MenuItem>
          <MenuItem value="failed">Prüfung nicht bestanden</MenuItem>
          <MenuItem value="canceledByStudent">Fahrschüler hat Prüfung abgesagt</MenuItem>
          <MenuItem value="studentNoShow">Fahrschüler ist nicht erschienen</MenuItem>
        </Select>
      </FormControl>
      {isSubmitting && <CircularProgress size={24} sx={{ mt: "6px" }} />}
    </Row>
  );
}

function _LinkToInstructor(props: { instructorId: string }) {
  const { instructorId } = props;
  return (
    <Link to={`/instructors/${instructorId}`} sx={{ display: "flex", alignItems: "center" }}>
      <InstructorAvatar instructorId={instructorId} size="20px" />
      <InstructorDisplayName sx={{ ml: ".5em" }} instructorId={instructorId} />
    </Link>
  );
}

function _LinkToStudent(props: { studentId: string }) {
  const { studentId } = props;
  return (
    <Link to={`/students/${studentId}`} style={{ display: "flex", alignItems: "center" }}>
      <StudentAvatar studentId={studentId} size="20px" />
      <Column sx={{ ml: ".5em" }}>
        <StudentLabels studentId={studentId} size="small" />
        <StudentDisplayName studentId={studentId} />
      </Column>
    </Link>
  );
}

function _VehicleTooltip(props: { vehicleId: string }) {
  const { vehicleId } = props;
  const { data: vehicle } = useGetOne<Vehicle>("vehicles", { id: vehicleId });
  switch (vehicle?.type) {
    case "car":
      return "PKW";
    case "motorcycle":
      return "Motorrad für Fahrschüler";
    case "bus":
      return "Bus";
    case "truck":
      return "LKW";
    case "tractor":
      return "Traktor";
    default:
      return "Fahrzeug";
  }
}

function _Vehicle2Tooltip(props: { vehicle2Id: string }) {
  const { vehicle2Id } = props;
  const { data: vehicle2 } = useGetOne<Vehicle>("vehicles", { id: vehicle2Id });
  switch (vehicle2?.type) {
    case "trailer":
      return "Anhänger";
    default:
      return "Begleitfahrzeug";
  }
}

function _LinkToVehicle(props: { vehicleId: string; highlightColor?: string }) {
  const { vehicleId, highlightColor } = props;
  const theme = useTheme();
  const { data: vehicle } = useGetOne<Vehicle>("vehicles", { id: vehicleId });
  const getRecordRepresentation = useGetRecordRepresentation("vehicles");

  if (!vehicle) {
    return null;
  }

  return (
    <Link to={`/vehicles/${vehicleId}`} style={{ display: "flex", alignItems: "center" }}>
      <VehicleIcon vehicle={vehicle} fontSize="small" htmlColor={highlightColor ?? theme.palette.text.secondary} />
      <Typography sx={{ ml: ".5em" }} variant="body2">
        {getRecordRepresentation(vehicle)}
      </Typography>
    </Link>
  );
}
